import { MarkdownEditor } from '@/components/MarkdownEditor'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useCreateIntent } from '@/hooks'
import type { CreateIntentRequest } from '@/types'
import { Check } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'

const NAME_MAX = 256
const EPIC_MAX = 256
const FEATURE_MAX = 256
const DESCRIPTION_MAX = 50000

interface CreateIntentDialogProps {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onIntentCreated?: () => void
}

export function CreateIntentDialog({ projectId, open, onOpenChange, onIntentCreated }: CreateIntentDialogProps) {
  const createIntent = useCreateIntent(projectId)

  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm<CreateIntentRequest>({
    defaultValues: { name: '', epic: '', feature: '', description: '' },
  })

  const nameValue = watch('name') ?? ''
  const epicValue = watch('epic') ?? ''
  const featureValue = watch('feature') ?? ''
  const descriptionValue = watch('description') ?? ''

  const onSubmit = async (data: CreateIntentRequest) => {
    try {
      await createIntent.mutateAsync({
        name: data.name.trim(),
        epic: data.epic?.trim() || undefined,
        feature: data.feature.trim(),
        description: data.description?.trim() || undefined,
      })
      toast.success('Intent created successfully')
      reset()
      onOpenChange(false)
      onIntentCreated?.()
    } catch {
      toast.error('Failed to create intent')
    }
  }

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      reset()
    }
    onOpenChange(isOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[100vw] w-[100vw] h-[100vh] max-h-[100vh] flex flex-col p-0 rounded-none border-none gap-0">
        <DialogHeader className="px-8 py-4 border-b shrink-0 bg-background">
          <DialogTitle className="text-xl font-bold">Create New Intent</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 flex flex-col min-h-0 bg-input-background">
            <div className="max-w-4xl mx-auto py-8 px-6 space-y-4 flex flex-col flex-1 min-h-0 w-full">
              <div className="space-y-2 shrink-0">
                <div className="flex items-center justify-between">
                  <Label htmlFor="intent-name">Name <span className="text-red-500">*</span></Label>
                  <span className="text-xs text-muted-foreground">{nameValue.trim().length}/{NAME_MAX}</span>
                </div>
                <Input
                  id="intent-name"
                  data-testid="intent-name-input"
                  placeholder="e.g. User authentication improvements"
                  {...register('name', {
                    required: 'Name is required',
                    validate: (value) => {
                      const trimmed = value.trim()
                      if (trimmed.length === 0) return 'Name is required'
                      if (trimmed.length > NAME_MAX) return `Name must be at most ${NAME_MAX} characters`
                      return true
                    },
                  })}
                />
                {errors.name && (
                  <p className="text-sm text-destructive" role="alert">{errors.name.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 shrink-0">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="intent-epic">Epic</Label>
                    <span className="text-xs text-muted-foreground">{epicValue.trim().length}/{EPIC_MAX}</span>
                  </div>
                  <Input
                    id="intent-epic"
                    data-testid="intent-epic-input"
                    placeholder="e.g. Identity"
                    {...register('epic', {
                      validate: (value) => {
                        const trimmed = (value ?? '').trim()
                        if (trimmed.length > EPIC_MAX) return `Epic must be at most ${EPIC_MAX} characters`
                        return true
                      },
                    })}
                  />
                  {errors.epic && (
                    <p className="text-sm text-destructive" role="alert">{errors.epic.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="intent-feature">Feature <span className="text-red-500">*</span></Label>
                    <span className="text-xs text-muted-foreground">{featureValue.trim().length}/{FEATURE_MAX}</span>
                  </div>
                  <Input
                    id="intent-feature"
                    data-testid="intent-feature-input"
                    placeholder="e.g. Logistics"
                    {...register('feature', {
                      required: 'Feature is required',
                      validate: (value) => {
                        const trimmed = value.trim()
                        if (trimmed.length === 0) return 'Feature is required'
                        if (trimmed.length > FEATURE_MAX) return `Feature must be at most ${FEATURE_MAX} characters`
                        return true
                      },
                    })}
                  />
                  {errors.feature && (
                    <p className="text-sm text-destructive" role="alert">{errors.feature.message}</p>
                  )}
                </div>
              </div>

              <div className="flex-1 flex flex-col min-h-0 space-y-2">
                <div className="flex items-center justify-between shrink-0">
                  <Label htmlFor="intent-description">Description</Label>
                  <span className="text-xs text-muted-foreground">{descriptionValue.trim().length}/{DESCRIPTION_MAX}</span>
                </div>
                <Controller
                  name="description"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (value && value.trim().length > DESCRIPTION_MAX)
                        return `Description must be at most ${DESCRIPTION_MAX} characters`
                      return true
                    },
                  }}
                  render={({ field }) => (
                    <MarkdownEditor
                      id="intent-description"
                      dataTestId="intent-description-input"
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      placeholder="e.g. Describe what this intent covers..."
                      fillHeight
                    />
                  )}
                />
                {errors.description && (
                  <p className="text-sm text-destructive" role="alert">{errors.description.message}</p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="px-8 py-4 border-t shrink-0 bg-background">
            <Button type="button" variant="ghost" onClick={() => handleClose(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createIntent.isPending}
            >
              <Check className="h-4 w-4" />
              {createIntent.isPending ? 'Creating...' : 'Create Intent'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
